<template>
  <div class="vfp">
    <!-- Header : back + variable identity -->
    <div class="vfp-head">
      <div
        role="button"
        tabindex="0"
        class="vfp-back"
        :aria-label="t('templateBuilder.variableFormat.back')"
        @click="$emit('back')"
        @keydown.enter="$emit('back')"
      >
        <i class="fa-light fa-arrow-left" />
      </div>
      <div class="vfp-head-info">
        <span class="vfp-head-label">{{ variable.displayLabel || t(variable.label) }}</span>
        <span class="vfp-head-value">{{ variable.value }}</span>
      </div>
      <span
        v-if="typeBadge"
        class="vfp-type-badge"
      >{{ typeBadge }}</span>
    </div>

    <div class="vfp-body">
      <div class="vfp-section-title">
        {{ t('templateBuilder.variableFormat.formatLabel') }}
      </div>

      <div
        v-for="entry in formatEntries"
        :key="entry.kind"
      >
        <div
          class="vfp-card"
          :class="{ 'vfp-card--selected': config.format === entry.kind }"
          @click="config.format = entry.kind"
        >
          <span class="vfp-radio" />
          <div class="vfp-card-text">
            <b>{{ entry.label }}</b>
            <small>{{ entry.hint }}</small>
          </div>
          <span
            v-if="suggested === entry.kind"
            class="vfp-suggested"
          >{{ t('templateBuilder.variableFormat.suggested') }}</span>
        </div>

        <!-- Currency options -->
        <div
          v-if="entry.kind === 'currency' && config.format === 'currency'"
          class="vfp-options"
        >
          <div class="vfp-opt-row">
            <span class="vfp-opt-label">{{ t('templateBuilder.variableFormat.currencyLabel') }}</span>
            <span
              class="vfp-pill"
              :class="{ 'vfp-pill--selected': !useCustomCurrency }"
              @click="setShopCurrency"
            >{{ t('templateBuilder.variableFormat.currencyShop') }} ({{ shopCurrency }})</span>
            <span
              class="vfp-pill"
              :class="{ 'vfp-pill--selected': useCustomCurrency }"
              @click="useCustomCurrency = true"
            >{{ t('templateBuilder.variableFormat.currencyOther') }}</span>
            <Dropdown
              v-if="useCustomCurrency"
              v-model="config.currencyCode"
              :options="currencyCodes"
              filter
              class="vfp-currency-dropdown"
              :placeholder="shopCurrency"
            />
          </div>
          <div
            v-if="!useCustomCurrency"
            class="vfp-opt-row"
          >
            <span class="vfp-opt-label">{{ t('templateBuilder.variableFormat.langLabel') }}</span>
            <span
              class="vfp-pill"
              :class="{ 'vfp-pill--selected': config.currencyLang === 'locale' }"
              @click="config.currencyLang = 'locale'"
            >{{ t('templateBuilder.variableFormat.langContact') }}</span>
            <span
              class="vfp-pill"
              :class="{ 'vfp-pill--selected': config.currencyLang === 'shop' }"
              @click="config.currencyLang = 'shop'"
            >{{ t('templateBuilder.variableFormat.langShop') }}</span>
          </div>
          <div class="vfp-opt-row">
            <span class="vfp-opt-label">{{ t('templateBuilder.variableFormat.conversionLabel') }}</span>
            <span>×</span>
            <InputText
              v-model="config.multiplyFactor"
              class="vfp-factor-input"
              placeholder="1"
            />
            <small class="vfp-opt-hint">{{ t('templateBuilder.variableFormat.conversionHint') }}</small>
          </div>
        </div>

        <!-- Date options -->
        <div
          v-if="entry.kind === 'date' && config.format === 'date'"
          class="vfp-options"
        >
          <div class="vfp-opt-row">
            <span
              v-for="preset in datePresets"
              :key="preset.key"
              class="vfp-pill"
              :class="{ 'vfp-pill--selected': config.datePreset === preset.key }"
              @click="config.datePreset = preset.key"
            >{{ preset.label }}</span>
          </div>
          <div
            v-if="config.datePreset === 'custom'"
            class="vfp-opt-row"
          >
            <span class="vfp-opt-label">{{ t('templateBuilder.variableFormat.datePresetCustom') }}</span>
            <InputText
              v-model="config.dateCustomTokens"
              class="vfp-tokens-input"
              placeholder="DD/MM/YYYY"
            />
          </div>
          <div
            v-if="config.datePreset === 'short' || config.datePreset === 'datetime'"
            class="vfp-opt-row"
          >
            <span class="vfp-opt-label">{{ t('templateBuilder.variableFormat.langLabel') }}</span>
            <span
              class="vfp-pill"
              :class="{ 'vfp-pill--selected': config.dateLang === 'locale' }"
              @click="config.dateLang = 'locale'"
            >{{ t('templateBuilder.variableFormat.langContact') }}</span>
            <span
              class="vfp-pill"
              :class="{ 'vfp-pill--selected': config.dateLang === 'shop' }"
              @click="config.dateLang = 'shop'"
            >{{ t('templateBuilder.variableFormat.langShop') }}</span>
          </div>
        </div>

        <!-- Text case options -->
        <div
          v-if="entry.kind === 'text' && config.format === 'text'"
          class="vfp-options"
        >
          <div class="vfp-opt-row">
            <span
              class="vfp-pill"
              :class="{ 'vfp-pill--selected': config.caseMode === 'upper' }"
              @click="config.caseMode = 'upper'"
            >{{ t('templateBuilder.variableFormat.caseUpper') }}</span>
            <span
              class="vfp-pill"
              :class="{ 'vfp-pill--selected': config.caseMode === 'lower' }"
              @click="config.caseMode = 'lower'"
            >{{ t('templateBuilder.variableFormat.caseLower') }}</span>
            <span
              class="vfp-pill"
              :class="{ 'vfp-pill--selected': config.caseMode === 'capitalize' }"
              @click="config.caseMode = 'capitalize'"
            >{{ t('templateBuilder.variableFormat.caseCapitalize') }}</span>
          </div>
        </div>

        <!-- Truncate options -->
        <div
          v-if="entry.kind === 'truncate' && config.format === 'truncate'"
          class="vfp-options"
        >
          <div class="vfp-opt-row">
            <span class="vfp-opt-label">{{ t('templateBuilder.variableFormat.truncateLabel') }}</span>
            <InputNumber
              v-model="config.truncateLimit"
              :min="1"
              :max="1000"
              show-buttons
              class="vfp-truncate-input"
            />
          </div>
        </div>
      </div>

      <div class="vfp-section-title">
        {{ t('templateBuilder.variableFormat.defaultLabel') }}
      </div>
      <InputText
        v-model="config.defaultValue"
        class="vfp-default-input"
        :placeholder="t('templateBuilder.variableFormat.defaultPlaceholder')"
      />

      <!-- Live preview -->
      <div class="vfp-preview">
        <span class="vfp-preview-label">{{ t('templateBuilder.variableFormat.previewLabel') }}</span>
        <span class="vfp-preview-value">{{ previewValue }}</span>
        <code class="vfp-preview-tag">{{ generatedTag }}</code>
      </div>
    </div>

    <div class="vfp-actions">
      <Button
        :label="t('templateBuilder.variableFormat.back')"
        text
        class="p-button-secondary"
        @click="$emit('back')"
      />
      <Button
        :label="t('templateBuilder.variableFormat.insert')"
        @click="$emit('insert', generatedTag)"
      />
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  PropType,
  computed,
  reactive,
  ref,
  watch,
} from 'vue';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import Dropdown from 'primevue/dropdown';
import Button from 'primevue/button';
import { useI18n } from 'vue-i18n';
import { EnrichedVariable } from '@/components/template-builder/utils/variables-list';
import { formatVariableValue, VariableFormatOptions } from '@/components/template-builder/utils/sanitizer';
import { UserState } from '@/composables/User';
import {
  VariableFormatKind,
  VariableFormatConfig,
  createDefaultFormatConfig,
  buildConfigModifiers,
  buildVariableTag,
  parseVariableValue,
  suggestedFormatFor,
} from './variable-format';

export default defineComponent({
  name: 'VariableFormatPanel',

  components: {
    InputText,
    InputNumber,
    Dropdown,
    Button,
  },

  props: {
    variable: {
      type: Object as PropType<EnrichedVariable>,
      required: true,
    },
  },

  emits: {
    insert: (value: string) => typeof value === 'string',
    back: () => true,
  },

  setup(props) {
    const { t, locale } = useI18n();

    const config = reactive<VariableFormatConfig>(createDefaultFormatConfig());
    const useCustomCurrency = ref(false);

    const suggested = computed(() => suggestedFormatFor(props.variable));

    // Reset the configuration each time the panel opens on another variable, pre-selecting the suggested format
    watch(
      () => props.variable,
      () => {
        Object.assign(config, createDefaultFormatConfig());
        useCustomCurrency.value = false;
        if (suggested.value) {
          config.format = suggested.value;
        }
      },
      { immediate: true },
    );

    const shopCurrency = computed(() => UserState.activeShop?.currency || 'EUR');
    const shopLang = computed(() => UserState.activeShop?.langs?.find((lang) => lang.default)?.id || locale.value);

    let currencyCodesCache: string[] | null = null;
    const currencyCodes = computed<string[]>(() => {
      if (currencyCodesCache === null) {
        try {
          currencyCodesCache = (Intl as any).supportedValuesOf('currency');
        } catch (error) {
          currencyCodesCache = ['EUR', 'USD', 'GBP', 'CHF', 'CAD'];
        }
      }
      return currencyCodesCache as string[];
    });

    const setShopCurrency = () => {
      useCustomCurrency.value = false;
      config.currencyCode = '';
    };

    // All formats stay available for every variable — only the preformatted one is hidden (§ anti double-formatting)
    const formatEntries = computed(() => {
      const entries: { kind: VariableFormatKind; label: string; hint: string }[] = [
        { kind: 'none', label: t('templateBuilder.variableFormat.fmtNone'), hint: t('templateBuilder.variableFormat.fmtNoneHint') },
        { kind: 'currency', label: t('templateBuilder.variableFormat.fmtCurrency'), hint: t('templateBuilder.variableFormat.fmtCurrencyHint') },
        { kind: 'date', label: t('templateBuilder.variableFormat.fmtDate'), hint: t('templateBuilder.variableFormat.fmtDateHint') },
        { kind: 'text', label: t('templateBuilder.variableFormat.fmtText'), hint: t('templateBuilder.variableFormat.fmtTextHint') },
        { kind: 'truncate', label: t('templateBuilder.variableFormat.fmtTruncate'), hint: t('templateBuilder.variableFormat.fmtTruncateHint') },
      ];

      const filtered = entries.filter((entry) => {
        if (props.variable.preformatted === 'currency' && entry.kind === 'currency') {
          return false;
        }
        if (props.variable.preformatted === 'date' && entry.kind === 'date') {
          return false;
        }
        return true;
      });

      // The suggested format is displayed right after "none"
      if (suggested.value) {
        const suggestedIndex = filtered.findIndex((entry) => entry.kind === suggested.value);
        if (suggestedIndex > 1) {
          const [suggestedEntry] = filtered.splice(suggestedIndex, 1);
          filtered.splice(1, 0, suggestedEntry);
        }
      }

      return filtered;
    });

    const datePresets = computed(() => ([
      { key: 'short' as const, label: t('templateBuilder.variableFormat.datePresetShort') },
      { key: 'long' as const, label: t('templateBuilder.variableFormat.datePresetLong') },
      { key: 'datetime' as const, label: t('templateBuilder.variableFormat.datePresetDatetime') },
      { key: 'custom' as const, label: t('templateBuilder.variableFormat.datePresetCustomPill') },
    ]));

    const typeBadge = computed(() => {
      if (props.variable.preformatted) {
        return t('templateBuilder.variableFormat.preformattedBadge');
      }
      if (props.variable.type && ['number', 'decimal'].includes(props.variable.type)) {
        return t('templateBuilder.variableFormat.typeNumber');
      }
      if (props.variable.type && ['date', 'datetime'].includes(props.variable.type)) {
        return t('templateBuilder.variableFormat.typeDate');
      }
      return null;
    });

    const generatedTag = computed(() => buildVariableTag(props.variable.value, config));

    // Live preview : the exact same formatter as the template preview (sanitizer mirror of the backend)
    const previewValue = computed(() => {
      const sampleByFormat: Record<string, string> = {
        currency: '89.99',
        date: new Date().toISOString(),
      };
      let sample = sampleByFormat[config.format];
      if (sample === undefined) {
        if (props.variable.type && ['number', 'decimal'].includes(props.variable.type)) {
          sample = '89.99';
        } else if (props.variable.type && ['date', 'datetime'].includes(props.variable.type)) {
          sample = new Date().toISOString();
        } else {
          sample = t('templateBuilder.variableFormat.sampleText');
        }
      }

      const options: VariableFormatOptions = {
        contactLang: locale.value,
        shopLang: shopLang.value,
        shopCurrency: shopCurrency.value,
      };
      const modifiers = buildConfigModifiers(config);
      const variableKey = parseVariableValue(props.variable.value).path.split('.').pop() || '';

      return formatVariableValue(variableKey, sample, modifiers.length ? modifiers : null, {}, options);
    });

    return {
      t,
      config,
      useCustomCurrency,
      suggested,
      shopCurrency,
      currencyCodes,
      setShopCurrency,
      formatEntries,
      datePresets,
      typeBadge,
      generatedTag,
      previewValue,
    };
  },
});
</script>

<style>
/* Format panel — NOT scoped because the parent PrimeVue Dialog teleports to <body> */
.vfp {
  display: flex;
  flex-direction: column;
  text-align: left;
}
.vfp-head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 0 12px;
  border-bottom: 1px solid #e5e7eb;
}
.vfp-back {
  border: 1px solid #e5e7eb;
  background: #fff;
  border-radius: 6px;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #6b7280;
  flex-shrink: 0;
}
.vfp-back:hover {
  background: #f9fafb;
}
.vfp-head-info {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
  flex: 1;
}
.vfp-head-label {
  font-size: 0.88rem;
  font-weight: 600;
  color: #1f2937;
}
.vfp-head-value {
  font-size: 0.7rem;
  color: #9ca3af;
  font-family: 'SF Mono', Monaco, 'Courier New', monospace;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.vfp-type-badge {
  font-size: 0.6rem;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 10px;
  background: #e0f2fe;
  color: #0369a1;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  white-space: nowrap;
  flex-shrink: 0;
}

.vfp-body {
  max-height: 380px;
  overflow-y: auto;
  padding: 12px 2px 4px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.vfp-section-title {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #9ca3af;
  margin: 4px 0 2px;
}

.vfp-card {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 9px 12px;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  transition: border-color 0.1s, background 0.1s;
}
.vfp-card:hover {
  background: #f9fafb;
}
.vfp-card--selected {
  border-color: #7c3aed;
  background: #faf8ff;
  box-shadow: 0 0 0 2px #ede9fe;
}
.vfp-radio {
  width: 15px;
  height: 15px;
  border-radius: 50%;
  border: 2px solid #d1d5db;
  flex-shrink: 0;
  background: #fff;
}
.vfp-card--selected .vfp-radio {
  border: 5px solid #7c3aed;
}
.vfp-card-text {
  display: flex;
  flex-direction: column;
  gap: 0;
  min-width: 0;
}
.vfp-card-text b {
  font-size: 0.83rem;
  font-weight: 600;
  color: #1f2937;
}
.vfp-card-text small {
  font-size: 0.72rem;
  color: #6b7280;
}
.vfp-suggested {
  margin-left: auto;
  font-size: 0.6rem;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 10px;
  background: #e0f2fe;
  color: #0369a1;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  flex-shrink: 0;
}

.vfp-options {
  border: 1px dashed #ddd3fb;
  border-radius: 8px;
  background: #fdfcff;
  padding: 10px 12px;
  margin: 6px 0 2px 25px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.vfp-opt-row {
  display: flex;
  align-items: center;
  gap: 7px;
  flex-wrap: wrap;
  font-size: 0.8rem;
  color: #374151;
}
.vfp-opt-label {
  font-size: 0.78rem;
  color: #6b7280;
}
.vfp-opt-hint {
  font-size: 0.7rem;
  color: #9ca3af;
}
.vfp-pill {
  border: 1px solid #e5e7eb;
  border-radius: 100px;
  padding: 3px 11px;
  font-size: 0.75rem;
  background: #fff;
  cursor: pointer;
  user-select: none;
  transition: border-color 0.1s, background 0.1s;
}
.vfp-pill:hover {
  border-color: #c4b5fd;
}
.vfp-pill--selected {
  border-color: #7c3aed;
  background: #ede9fe;
  color: #6d28d9;
  font-weight: 600;
}
.vfp-factor-input {
  width: 70px;
}
.vfp-factor-input.p-inputtext,
.vfp-factor-input .p-inputtext {
  padding: 4px 8px;
  font-size: 0.8rem;
}
.vfp-tokens-input {
  width: 150px;
  font-family: 'SF Mono', Monaco, 'Courier New', monospace;
}
.vfp-truncate-input .p-inputtext {
  width: 70px;
  padding: 4px 8px;
  font-size: 0.8rem;
}
.vfp-currency-dropdown {
  min-width: 110px;
}
.vfp-currency-dropdown .p-inputtext {
  padding: 4px 8px;
  font-size: 0.8rem;
}
.vfp-default-input {
  font-size: 0.8rem;
}

.vfp-preview {
  background: #f8f7fc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px 13px;
  margin-top: 6px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.vfp-preview-label {
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #9ca3af;
}
.vfp-preview-value {
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
}
.vfp-preview-tag {
  font-size: 0.7rem;
  color: #7c3aed;
  font-family: 'SF Mono', Monaco, 'Courier New', monospace;
  word-break: break-all;
}

.vfp-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 12px;
}
</style>
